type InferenceLike = {
  inferenceResultId?: string
  modelBindingGroupVersionTransformationId?: string
  jsonPath?: string
  value: string
  inferenceResultValue?: unknown
  latestChangelog?: {
    patch?: {
      value?: unknown
    }
  }
}

type EvaluationWithUnexposedInferencesLike = {
  unexposedInferences?: InferenceLike[]
}

export type UpdateInferenceValueInput = {
  value: string
  inferenceResultId: string
  modelBindingGroupVersionTransformationId?: string
  jsonPath?: string
  inferenceResultValue?: unknown
}

const matchesInferenceUpdateTarget = ({
  inference,
  input,
}: {
  inference: Pick<
    InferenceLike,
    | 'inferenceResultId'
    | 'modelBindingGroupVersionTransformationId'
    | 'jsonPath'
  >
  input: Pick<
    UpdateInferenceValueInput,
    | 'inferenceResultId'
    | 'modelBindingGroupVersionTransformationId'
    | 'jsonPath'
  >
}) => {
  if (inference.inferenceResultId !== input.inferenceResultId) return false

  if (input.modelBindingGroupVersionTransformationId)
    return (
      inference.modelBindingGroupVersionTransformationId ===
      input.modelBindingGroupVersionTransformationId
    )

  return (
    !inference.modelBindingGroupVersionTransformationId &&
    !!input.jsonPath &&
    inference.jsonPath === input.jsonPath
  )
}

export const patchInferenceValueList = <T extends InferenceLike>(
  inferences: T[],
  input: UpdateInferenceValueInput,
) =>
  inferences.map((inference) => {
    if (!matchesInferenceUpdateTarget({ inference, input })) return inference

    return {
      ...inference,
      value: input.value,
      ...(typeof input.inferenceResultValue !== 'undefined'
        ? {
            inferenceResultValue: input.inferenceResultValue,
          }
        : {}),
      latestChangelog: {
        ...(inference.latestChangelog || {}),
        patch: {
          ...(inference.latestChangelog || {}).patch,
          value: input.value,
        },
      },
    }
  })

export const patchEvaluationUnexposedInferences = <
  T extends EvaluationWithUnexposedInferencesLike,
>(
  evaluations: T[],
  input: UpdateInferenceValueInput,
) =>
  evaluations.map((evaluation) => {
    if (!evaluation.unexposedInferences?.length) return evaluation

    return {
      ...evaluation,
      unexposedInferences: patchInferenceValueList(
        evaluation.unexposedInferences,
        input,
      ),
    }
  })
